/**
 * The stored half of an admin session.
 *
 * docs/04-API-SPEC.md §5 issues three kinds of bearer token and the difference
 * between them is the whole of the 2FA guarantee: a `two_factor_required` or
 * `two_factor_setup_required` token carries abilities that reach exactly one
 * endpoint, so a client that ignores `status` and stores the token anyway gets
 * a 403 rather than the dashboard. `App\Support\AdminTokens` says so in as many
 * words. This module keeps the status beside the token so the router can honour
 * it too, and the server stays the thing that actually enforces it.
 */
import { z } from 'zod';

/** Mirrors `AdminTokens::STATUS_*`. */
export const SESSION_STATUSES = [
  'authenticated',
  'two_factor_required',
  'two_factor_setup_required',
] as const;

export type SessionStatus = (typeof SESSION_STATUSES)[number];

const sessionSchema = z.object({
  token: z.string().min(1),
  status: z.enum(SESSION_STATUSES),
  /** Absolute ISO-8601, because §5 sends it that way — see below. */
  expiresAt: z.string().nullable(),
});

export type Session = z.infer<typeof sessionSchema>;

/*
 * Versioned, so a change to the shape logs everyone out rather than handing
 * `JSON.parse` something it will mis-read.
 */
const STORAGE_KEY = 'sjuni.admin.session.v1';

/**
 * Private-mode Safari throws on `localStorage`, and an admin who cannot sign in
 * because storage is unavailable is worse than one who signs in every visit.
 */
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readSession(): Session | null {
  const raw = storage()?.getItem(STORAGE_KEY);

  if (!raw) {
    return null;
  }

  const parsed = sessionSchema.safeParse(safeJson(raw));

  if (!parsed.success) {
    clearSession();

    return null;
  }

  /*
   * A token past its expiry is dropped here rather than sent and refused.
   * `expires_at` is absolute rather than a duration precisely so this check is
   * possible — a client with a skewed clock cannot decide a live token is dead,
   * and the server would disagree with it if it tried.
   */
  if (hasExpired(parsed.data)) {
    clearSession();

    return null;
  }

  return parsed.data;
}

export function writeSession(session: Session): void {
  storage()?.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  storage()?.removeItem(STORAGE_KEY);
}

export function hasExpired(session: Session, now: Date = new Date()): boolean {
  if (session.expiresAt === null) {
    return false;
  }

  const expiry = Date.parse(session.expiresAt);

  return Number.isFinite(expiry) && expiry <= now.getTime();
}

/** Only a full session may reach anything but the auth screens. */
export function isAuthenticated(session: Session | null): session is Session {
  return session?.status === 'authenticated';
}

/**
 * Where a session that is not yet full has to go next. Returning null for an
 * authenticated one keeps the caller from having to know the status values.
 */
export function pendingStep(session: Session | null): '/login/two-factor' | '/login/setup' | null {
  switch (session?.status) {
    case 'two_factor_required':
      return '/login/two-factor';
    case 'two_factor_setup_required':
      return '/login/setup';
    default:
      return null;
  }
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
